import { useQuery } from "@tanstack/react-query";

import ViewsService from "@/services/ViewsService";

import { useAuth } from "@/hooks/useAuth";

import { viewKeys } from "@/constants/CQueryKeys";

import { TView } from "@/types";

/** The user's own views first, then by name. */
export const sortViews =
	(userId?: number) =>
	(a: TView, b: TView): number => {
		const aMine = a.userId === userId ? 0 : 1;
		const bMine = b.userId === userId ? 0 : 1;
		if (aMine !== bMine) return aMine - bMine;
		return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
	};

const EMPTY: TView[] = [];

/**
 * The views the user can see - one shared query (`["views"]`,
 * the key every view mutation invalidates), sorted. What used to be
 * the app context's `views`.
 */
export const useViews = () => {
	const { isAuthenticated, user } = useAuth();
	const userId = user?.id;
	const query = useQuery({
		queryKey: viewKeys.all,
		queryFn: (): Promise<TView[]> => ViewsService.getViews(),
		enabled: isAuthenticated,
		select: (data) => [...data].sort(sortViews(userId)),
		// Read by many components at once (every day header, the list, the
		// sidebar): without this each new instance refetched the list on
		// mount. Every view mutation invalidates the key explicitly.
		staleTime: 5 * 60 * 1000,
	});
	return {
		views: query.data ?? EMPTY,
		isLoading: query.isLoading,
		isFetched: query.isFetched,
		refetch: query.refetch,
	};
};
